// Every action and state snapshot that reaches a console -- the redux-logger transformers and the
// loading timeout message -- goes through here first, so no proof value (a password, an MFA
// verification code) and no token is ever printed.

export const REDACTED = '[redacted]'

// Matched as a case insensitive substring of the key name, so `currentPassword`, `newPassword`,
// `refresh_token`, `currentMfaProof`, `verificationCode` and an `Authorization` header all match
// without maintaining an exhaustive list of field names.
const SENSITIVE_KEY_FRAGMENTS = ['password', 'verificationcode', 'mfaproof', 'token', 'authorization'] as const

// `token` on its own would also swallow the token lifetime fields (`accessTokenExpireTimestamp`,
// `refreshTokenExpiresInSeconds`). They carry no credential and are exactly what a developer reads
// while debugging a refresh, so they stay readable.
const READABLE_KEY_FRAGMENTS = ['expire'] as const

export const isSensitiveKey = (key: string): boolean => {
  const name = key.toLowerCase()
  if (READABLE_KEY_FRAGMENTS.some((fragment) => name.includes(fragment))) {
    return false
  }
  return SENSITIVE_KEY_FRAGMENTS.some((fragment) => name.includes(fragment))
}

const isPlainObject = (value: object): boolean => {
  const prototype = Object.getPrototypeOf(value)
  return prototype === Object.prototype || prototype === null
}

// `copies` keeps one copy per source object, which both terminates cycles and keeps a shared
// subtree from being walked twice -- this runs on every dispatched action while logging is on.
const redactValue = (value: unknown, copies: WeakMap<object, unknown>): unknown => {
  if (value === null || typeof value !== 'object') {
    return value
  }

  const source = value as object
  if (copies.has(source)) {
    return copies.get(source)
  }

  if (Array.isArray(source)) {
    const copy: unknown[] = []
    copies.set(source, copy)
    source.forEach((item) => {
      copy.push(redactValue(item, copies))
    })
    return copy
  }

  // An Error is reduced to an allowlist before the pass-through below can reach it. `baseRequest`
  // hands the saga the raw axios error, and `error.config.data` is the serialized request body --
  // for `identity_authenticate` that is the login password, and `config.headers` the bearer token.
  // Copying only what a developer debugs from means a field axios adds later cannot leak either.
  if (source instanceof Error) {
    const copy: Record<string, unknown> = {name: source.name, message: source.message, stack: source.stack}
    copies.set(source, copy)
    return copy
  }

  if (!isPlainObject(source)) {
    // A Date, a Map or a class instance is handed to the logger untouched: it carries no proof
    // field, and copying it would cost its identity.
    return source
  }

  const copy: Record<string, unknown> = {}
  copies.set(source, copy)
  Object.entries(source as Record<string, unknown>).forEach(([key, entry]) => {
    copy[key] = isSensitiveKey(key) ? REDACTED : redactValue(entry, copies)
  })
  return copy
}

export const redactForLogging = (value: unknown): unknown => {
  return redactValue(value, new WeakMap<object, unknown>())
}
